import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { TaxonomyFocus } from './components/TaxonomyFocus';
import { TaxonomyInspector } from './components/TaxonomyInspector';
import { TaxonomyMap } from './components/TaxonomyMap';
import { TaxonomyTree } from './components/TaxonomyTree';
import { NewPartDialog } from './components/NewPartDialog';
import { KindIcon } from './components/icons';
import { FlowDesigner } from './components/FlowDesigner';
import { api, ApiError } from './lib/api';
import { useT, type MessageKey } from './lib/i18n';
import {
  DIRECTIVE_LISTS, EDGE_KINDS, buildGraph, listOf, packagesOf, treeOf, valuesOf,
  type EdgeKind, type TaxGraph, type TaxNode
} from './lib/taxonomyGraph';
import { actionsOf, actionsOnFlow, flowOf, isAutomation } from './lib/flowDesign';
import {
  labelOf, type ChangeView, type CurrentDefinition, type DraftView, type ProblemView, type RegistryDefinition
} from './lib/registry';
import './taxonomy.css';

type Mode = 'map' | 'focus' | 'list';
const DEFAULT_LINES: EdgeKind[] = ['extends', 'refers', 'holds', 'link', 'applies', 'flow', 'opens', 'uses'];

/** What each part of a definition is, as JSON, by the id the graph gives it: to say what the draft changed. */
function partsOf(definition: RegistryDefinition | null | undefined): Map<string, string> {
  const out = new Map<string, string>();
  if (!definition) return out;
  for (const type of definition.types) out.set(`template:${type.key}`, JSON.stringify(type));
  for (const value of valuesOf(definition)) out.set(`value:${value.key}`, JSON.stringify(value));
  for (const list of DIRECTIVE_LISTS) for (const part of listOf(definition, list)) out.set(`${list}:${part.key}`, JSON.stringify(part));
  return out;
}

/** A small panel that opens under its button and closes when anything else is clicked. */
function Popover({ label, children, testid, active }: { label: ReactNode; children: ReactNode; testid: string; active?: boolean }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div className="tx-pop" ref={box}>
      <button className={`tx-pop-button${active ? ' active' : ''}`} aria-expanded={open} data-testid={testid} onClick={() => setOpen(!open)}>
        {label} <span className="tx-caret">▾</span>
      </button>
      {open && <div className="tx-pop-panel">{children}</div>}
    </div>
  );
}

/**
 * The taxonomy screen (docs/en/43 sec.8): the platform's release - every template of the four
 * roots, the value types, every directive, the packages - and what is chosen in it edited beside it.
 *
 * <p>Three ways to look at it. The map: a column per root and per branch of the directives, as
 * long as the taxonomy is - the page scrolls - with the kinds of directive folded to their name and
 * count and only the chosen part's lines drawn. Focus: one part with what it stands on and what
 * stands on it, however large the rest; a flow in focus is its designer. The list: the same as a
 * tree. Something new is made from a dialog that asks only what that kind needs.
 *
 * <p>The edits are a draft: saved, the server says what they change and what they would break;
 * published, they are the next release.
 */
export function TaxonomyPage() {
  const { t, locale } = useT();
  const [current, setCurrent] = useState<CurrentDefinition | null>(null);
  const [draft, setDraft] = useState<(DraftView & { definition?: RegistryDefinition }) | null>(null);
  const [working, setWorking] = useState<RegistryDefinition | null>(null);
  const [mode, setMode] = useState<Mode>('map');
  const [selected, setSelectedRaw] = useState<string | null>(null);
  const [trail, setTrail] = useState<string[]>([]);
  const [designerAction, setDesignerAction] = useState<string | null>(null);
  const [packs, setPacks] = useState<Set<string>>(new Set());
  const [show, setShow] = useState<Set<EdgeKind>>(new Set(DEFAULT_LINES));
  const [allLines, setAllLines] = useState(false);
  const [compact, setCompact] = useState(false);
  const [side, setSide] = useState(true);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [problems, setProblems] = useState<ProblemView[]>([]);
  const [report, setReport] = useState<DraftView | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'bad'; text: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [making, setMaking] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [published, saved] = await Promise.all([api.registryDefinition(), api.registryDraft()]);
      setCurrent(published);
      setDraft(saved);
      setWorking(structuredClone(saved?.definition ?? published.definition));
      setReport(saved);
      setProblems([]);
      // Something chosen to begin with, so that the map shows its lines: GeoNexora's layer template if it has one.
      const types = (saved?.definition ?? published.definition).types;
      const first = types.find((x) => x.key === 'gx.platform-layer') ?? types[0];
      setSelectedRaw((was) => was ?? (first ? `template:${first.key}` : null));
    } catch (e) {
      setLoadError((e as Error).message);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const graph = useMemo(() => buildGraph(working, locale), [working, locale]);
  const tree = useMemo(() => treeOf(graph), [graph]);
  const allPacks = useMemo(() => packagesOf(working), [working]);
  const query = search.trim().toLowerCase();
  const visible = useCallback((n: TaxNode) => packs.size === 0 || packs.has(n.pack), [packs]);
  const matches = useMemo(() => (query ? new Set(graph.nodes.filter((n) => visible(n)
    && (n.key.toLowerCase().includes(query) || n.label.toLowerCase().includes(query))).map((n) => n.id)) : null), [graph, query, visible]);
  const troubled = useMemo(() => {
    const out = new Set<string>();
    for (const problem of problems) {
      const hit = [...graph.nodes].sort((a, b) => b.key.length - a.key.length)
        .find((n) => problem.where === n.key || problem.where.startsWith(`${n.key}.`));
      if (hit) out.add(hit.id);
    }
    return out;
  }, [problems, graph]);
  const changed = useMemo(() => {
    const before = partsOf(current?.definition);
    const out = new Map<string, 'new' | 'changed'>();
    for (const [id, json] of partsOf(working)) {
      const was = before.get(id);
      if (was === undefined) out.set(id, 'new');
      else if (was !== json) out.set(id, 'changed');
    }
    return out;
  }, [current, working]);

  const saved = draft?.definition ?? current?.definition;
  const dirty = working != null && saved != null && JSON.stringify(working) !== JSON.stringify(saved);

  function select(id: string | null, how: 'replace' | 'push' = 'push') {
    if (id && selected && id !== selected && mode === 'focus' && how === 'push') setTrail((was) => [...was, selected].slice(-8));
    if (how === 'replace') setTrail([]);
    setSelectedRaw(id);
    setDesignerAction(null);
    setSearching(false);
  }

  function openFocus(id: string, action?: string) {
    setTrail([]);
    setSelectedRaw(id);
    setDesignerAction(action ?? null);
    setMode('focus');
  }

  function change(next: RegistryDefinition) {
    setWorking(next);
    setNote(null);
  }

  async function saveDraft() {
    if (!working) return;
    setBusy(true);
    setNote(null);
    try {
      const answer = await api.saveRegistryDraft(working);
      setReport(answer);
      setDraft({ ...answer, definition: structuredClone(working) });
      setProblems([]);
      setNote({ kind: 'ok', text: t('taxonomy.saved', { kind: t(`types.kind.${answer.kind}` as MessageKey) }) });
    } catch (e) {
      if (e instanceof ApiError && e.problems.length > 0) {
        setProblems(e.problems);
        setNote({ kind: 'bad', text: t('types.refused', { count: e.problems.length }) });
      } else {
        setNote({ kind: 'bad', text: (e as Error).message });
      }
    } finally {
      setBusy(false);
    }
  }

  async function publish() {
    setBusy(true);
    try {
      const published = await api.publishRegistry();
      setNote({ kind: 'ok', text: t('taxonomy.published', { version: published.version }) });
      await load();
    } catch (e) {
      setNote({ kind: 'bad', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    setBusy(true);
    try {
      await api.discardRegistryDraft();
      await load();
      setNote(null);
    } catch (e) {
      setNote({ kind: 'bad', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <div className="gnx-tax"><div className="banner danger">{loadError}</div></div>;
  if (!working || !current) return <div className="gnx-tax"><div className="empty">{t('taxonomy.loading')}</div></div>;

  const node = selected ? graph.byId.get(selected) : undefined;
  const flowInFocus = mode === 'focus' && node?.kind === 'flows';
  const results = matches ? graph.nodes.filter((n) => matches.has(n.id)).slice(0, 10) : [];
  const status = dirty ? 'unsaved' : draft ? 'draft' : 'published';

  return (
    <div className="gnx-tax taxonomy-page" data-testid="taxonomy-page">
      <div className="tx-top">
        <div className="tx-title">
          <h1>{t('taxonomy.title')}</h1>
          <span className="hint">{t('tx.version', { version: current.version, count: graph.nodes.length })}</span>
        </div>
        <span className="spacer" />
        <div className={`tx-status tx-status-${status}`} data-testid="tx-draft">
          <span className="tx-status-dot" />
          <span>{t(`tx.status.${status}` as MessageKey)}</span>
          {changed.size > 0 && <span className="tx-count">{t('tx.status.changes', { count: changed.size })}</span>}
          {draft && <button className="ghost" disabled={busy} onClick={discard} data-testid="tx-discard">{t('taxonomy.discard')}</button>}
          <button disabled={busy || !dirty} onClick={saveDraft} data-testid="tx-save">{t('taxonomy.save')}</button>
          <button className="primary" disabled={busy || dirty || !draft || !(report?.publishable ?? false)}
                  onClick={publish} data-testid="tx-publish">{t('taxonomy.publish')}</button>
        </div>
        <button className="primary tx-new-button" data-testid="tx-new" onClick={() => setMaking(true)}>+ {t('tx.new.button')}</button>
      </div>

      {(note || problems.length > 0 || (report && !dirty && report.changes.length > 0)) && (
        <div className="tx-report">
          {note && <div className={`banner ${note.kind === 'ok' ? 'info' : 'danger'}`} data-testid="tx-note">{note.text}</div>}
          {problems.length > 0 && (
            <ul className="tx-problems" data-testid="tx-problems">
              {problems.map((p, i) => <li key={i}><code>{p.where}</code> {p.message}</li>)}
            </ul>
          )}
          {report && !dirty && report.changes.length > 0 && <Changes changes={report.changes} blocked={report.blockedBy} />}
        </div>
      )}

      <div className="tx-toolbar">
        <div className="view-switch" role="tablist">
          {(['map', 'focus', 'list'] as Mode[]).map((m) => (
            <button key={m} role="tab" aria-selected={mode === m} className={mode === m ? 'on' : ''}
                    data-testid={`tx-mode-${m}`} onClick={() => setMode(m)}>{t(`tx.mode.${m}` as MessageKey)}</button>
          ))}
        </div>
        <div className="tx-search-box">
          <input className="tx-search" placeholder={t('taxonomy.search')} value={search} data-testid="tx-search"
                 onFocus={() => setSearching(true)}
                 onChange={(e) => { setSearch(e.target.value); setSearching(true); }}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter' && results[0]) select(results[0].id);
                   if (e.key === 'Escape') { setSearch(''); setSearching(false); }
                 }} />
          {query && <span className="tx-search-count">{matches?.size ?? 0}</span>}
          {searching && results.length > 0 && (
            <ul className="tx-results" data-testid="tx-results" onMouseDown={(e) => e.preventDefault()}>
              {results.map((n) => (
                <li key={n.id}><button onClick={() => select(n.id)}>
                  <KindIcon kind={n.kind === 'template' ? n.lane : n.kind} />
                  <span>{n.label}</span><code>{n.key}</code>
                  <span className="hint">{n.kind === 'template' ? t(`taxonomy.lane.${n.lane}` as MessageKey) : t(`taxonomy.kind.${n.kind}` as MessageKey)}</span>
                </button></li>
              ))}
            </ul>
          )}
        </div>
        <Popover testid="tx-packs" active={packs.size > 0}
                 label={packs.size === 0 ? t('tx.packs.all') : t('tx.packs.some', { count: packs.size })}>
          <div className="tx-pop-list">
            {allPacks.map((p) => (
              <label key={p} className="field-check" data-testid={`tx-pack-${p}`}>
                <input type="checkbox" checked={packs.has(p)}
                       onChange={() => setPacks((was) => { const next = new Set(was); if (next.has(p)) next.delete(p); else next.add(p); return next; })} />
                {p}
              </label>
            ))}
            {packs.size > 0 && <button className="link" onClick={() => setPacks(new Set())}>{t('tx.packs.clear')}</button>}
          </div>
        </Popover>
        {mode === 'map' && (
          <Popover testid="tx-lines" label={t('taxonomy.lines')}>
            <div className="tx-pop-list">
              <label className="field-check"><input type="checkbox" checked={allLines} data-testid="tx-all-lines"
                     onChange={(e) => setAllLines(e.target.checked)} /> {t('tx.lines.all')}</label>
              <p className="hint">{t('tx.lines.hint')}</p>
              {EDGE_KINDS.map((k) => (
                <label key={k} className={`field-check tx-line-kind tx-wire-${k}`} data-testid={`tx-edge-${k}`}>
                  <input type="checkbox" checked={show.has(k)}
                         onChange={() => setShow((was) => { const next = new Set(was); if (next.has(k)) next.delete(k); else next.add(k); return next; })} />
                  <svg width="22" height="8" aria-hidden="true"><path d="M1 4 H21" className={`tx-wire tx-wire-${k} strong`} /></svg>
                  {t(`taxonomy.edge.${k}` as MessageKey)}
                </label>
              ))}
            </div>
          </Popover>
        )}
        {mode === 'map' && (
          <button className={`ghost${compact ? ' on-toggle' : ''}`} onClick={() => setCompact(!compact)} data-testid="tx-compact">
            {t(compact ? 'tx.roomy' : 'tx.compact')}</button>
        )}
        <span className="spacer" />
        {!flowInFocus && (
          <button className="ghost" onClick={() => setSide(!side)} data-testid="tx-side-toggle">{t(side ? 'tx.side.hide' : 'tx.side.show')}</button>
        )}
      </div>

      {mode === 'map' && <p className="hint tx-howto">{t('tx.map.hint')}</p>}

      {flowInFocus && node ? (
        <FlowDesigner definition={working} published={current.definition} flowKey={node.key} onChange={change}
                      initial={designerAction ? { kind: 'action', key: designerAction } : undefined}
                      onOpenPart={(id) => openFocus(id)} />
      ) : (
        <div className={`tx-body${side && selected ? '' : ' no-side'}`}>
          <div className="tx-canvas">
            {mode === 'map' && (
              <TaxonomyMap graph={graph} selected={selected} onSelect={(id) => select(id)} onOpen={(id) => openFocus(id)}
                           visible={visible} matches={matches} problems={troubled} changed={changed}
                           show={show} allLines={allLines} compact={compact} />
            )}
            {mode === 'focus' && (selected
              ? <TaxonomyFocus graph={graph} selected={selected} trail={trail} onSelect={(id) => select(id)}
                               onBack={(i) => { setSelectedRaw(trail[i]); setTrail(trail.slice(0, i)); }}
                               summary={summaryOf(graph, working, selected, locale, t)} />
              : <div className="empty">{t('taxonomy.choosePart')}</div>)}
            {mode === 'list' && (
              <>
                <TaxonomyTree groups={tree} selected={selected} onSelect={(id) => select(id)} problems={troubled} />
                {(working.links ?? []).length > 0 && (
                  <details className="tx-group" open>
                    <summary>{t('taxonomy.kinds.links')}</summary>
                    <ul className="tx-list">
                      {(working.links ?? []).map((l) => (
                        <li key={l.key}><button className="link" onClick={() => select(`link:${l.key}`)}>{l.key}</button> {l.from} → {l.to}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </>
            )}
          </div>
          {side && selected && (
            <aside className="tx-side">
              <TaxonomyInspector definition={working} published={current.definition} selected={selected}
                                 onChange={change} onSelect={(id) => select(id)}
                                 onOpenDesigner={(flowKey, action) => openFocus(`flows:${flowKey}`, action)} />
            </aside>
          )}
        </div>
      )}

      {making && (
        <NewPartDialog definition={working} about={node?.kind === 'template' ? node.key : undefined}
                       onCancel={() => setMaking(false)}
                       onMake={(next, id, focus) => {
                         change(next);
                         setMaking(false);
                         setSide(true);
                         if (focus) openFocus(id);
                         else if (id.startsWith('actions:')) {
                           const action = actionsOf(next).find((a) => `actions:${a.key}` === id);
                           const flow = flowOf(next, action?.target);
                           if (flow) openFocus(`flows:${flow.key}`, action!.key); else select(id, 'replace');
                         } else select(id, 'replace');
                       }} />
      )}
    </div>
  );
}

/** What a part is in a few words, for the middle of the focus view. */
function summaryOf(graph: TaxGraph, definition: RegistryDefinition, id: string, locale: string,
                   t: (key: MessageKey, vars?: Record<string, string | number>) => string): string[] {
  const node = graph.byId.get(id);
  if (!node) return [];
  if (node.kind === 'template') {
    const type = definition.types.find((x) => x.key === node.key);
    const out = [t('tx.sum.fields', { count: (type?.fields ?? []).length })];
    const flow = flowOf(definition, node.key);
    if (flow) out.push(t('tx.sum.flow', { flow: labelOf(flow.labels, locale as never, flow.key), count: flow.states.length }));
    return out;
  }
  if (node.kind === 'flows') {
    const flow = (definition.flows ?? []).find((f) => f.key === node.key);
    const actions = actionsOnFlow(definition, node.key);
    return flow ? [
      t('tx.sum.states', { count: flow.states.length }),
      (flow.transitions ?? []).length === 0 ? t('tx.flow.isOpen') : t('tx.sum.moves', { count: (flow.transitions ?? []).length }),
      t('tx.sum.buttons', { count: actions.filter((a) => !isAutomation(a)).length, autos: actions.filter(isAutomation).length })
    ] : [];
  }
  if (node.kind === 'actions') {
    const action = actionsOf(definition).find((a) => a.key === node.key);
    return action ? [t(isAutomation(action) ? 'tx.action.automatic' : 'tx.action.manual'), t('tx.sum.effects', { count: (action.effects ?? []).length })] : [];
  }
  return [];
}

function Changes({ changes, blocked }: { changes: ChangeView[]; blocked: ChangeView[] }) {
  const { t } = useT();
  return (
    <details className="tx-changes" open={blocked.length > 0} data-testid="tx-changes">
      <summary>{t('taxonomy.changes', { count: changes.length })}</summary>
      <ul>
        {changes.map((c, i) => (
          <li key={i} className={`change-${c.kind}${blocked.includes(c) ? ' blocked' : ''}`}>
            <span className="chip small">{t(`types.kind.${c.kind}` as MessageKey)}</span> <code>{c.where}</code> {c.what}
          </li>
        ))}
      </ul>
    </details>
  );
}

export default TaxonomyPage;
